import {
  type NavigateFunction,
  useFetcher,
  useLoaderData,
  useLocation,
  useNavigate,
} from '@remix-run/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { loader } from '~/routes/_index';
import type { Feed, FeedEntry } from '~/services/layout';
import { track } from '~/services/analytics/index.client';
import debounce from 'lodash.debounce';
import { type UseIsLoggedInReturnType } from './use-is-logged-in';

export const FAVORITE_DEBOUNCE_DELAY: number = 1000;

export type UseFavoriteActionReturnType = {
  isFavorite: boolean;
  favoriteAction: () => void;
  shouldRenderFavoriteAction: boolean;
  isLoginStatusPending: boolean;
};

export function useFavoriteAction({
  entry,
  favoritesFeed,
  isLoggedInData,
}: {
  entry: FeedEntry;
  favoritesFeed: Feed | undefined;
  isLoggedInData: UseIsLoggedInReturnType | undefined;
}): UseFavoriteActionReturnType {
  const { isFavoritesSyncedFeed } = useLoaderData<typeof loader>();

  const fetcher: any = useFetcher({
    key: 'favorites',
  });

  const [isFavorite, setIsFavorite] = useState<boolean | null>(null);

  const isLoggedIn = isLoggedInData?.isLoggedIn;
  const isLoginStatusPending = !!isLoggedInData?.isLoginStatusPending;

  const entryId: string = entry?.id;

  useEffect(() => {
    if (fetcher.state === 'loading') {
      return;
    }

    if (!isLoggedIn) {
      setIsFavorite(false);
      return;
    }

    if (favoritesFeed && fetcher.state === 'idle') {
      const isFav = favoritesFeed?.entry?.some(
        (fav: FeedEntry) => fav.id === entryId
      );
      setIsFavorite(!!isFav);
    }
  }, [isLoggedInData, favoritesFeed, entryId, fetcher.state]);

  const navigate: NavigateFunction = useNavigate();
  const location = useLocation();

  const debouncedFavoriteAction = useCallback(
    debounce(
      (newIsFavorite: boolean) => {
        const favAction = newIsFavorite ? 'added' : 'removed';

        const stringifyEntry: string = getStringifyEntry(entry);

        fetcher.submit(
          { favAction, stringifyEntry },
          {
            method: 'post',
            action: `/api/favorite-action`,
          }
        );

        track('favclicked789', { entryId, isFavorite: newIsFavorite });
      },
      FAVORITE_DEBOUNCE_DELAY,
      {
        leading: true,
      }
    ),
    [entry, fetcher, entryId]
  );

  const favoriteAction = useCallback(() => {
    if (!isLoggedIn) {
      navigate(
        `/login?redirectBack=${encodeURIComponent(
          location.pathname + location.search
        )}`
      );
      return;
    }

    const newIsFavorite = !isFavorite;

    setIsFavorite(newIsFavorite);
    debouncedFavoriteAction(newIsFavorite);
  }, [isLoggedIn, isFavorite, navigate, location, debouncedFavoriteAction]);

  const shouldRenderFavoriteAction = [
    !isLoginStatusPending,
    entryId,
    isFavorite !== null,
    isFavoritesSyncedFeed,
  ].every(Boolean);

  return useMemo(
    () => ({
      isFavorite: !!isFavorite,
      favoriteAction,
      shouldRenderFavoriteAction,
      isLoginStatusPending,
    }),
    [
      isFavorite,
      favoriteAction,
      shouldRenderFavoriteAction,
      isLoginStatusPending,
    ]
  );
}

/**
 * Get the stringified entry object with error handling
 * @param entry - The entry object
 * @returns - The stringified entry object or empty string
 */
function getStringifyEntry(entry: any): string {
  try {
    return JSON.stringify(entry);
  } catch (error) {
    return '';
  }
}
